import React, { FC, useMemo, useState } from 'react';

import { ListItemProps, Search, SelectList } from '@silverfort/sf-components';

import { useHistory, useRouteMatch } from 'react-router-dom';
import { useHandleStartPolicyFromTemplate } from './hooks/useHandleStartPolicyFromTemplate';
import { PolicyTemplate, PolicyTemplateStaticCategory } from '../../../types/policies';
import { getPolicyFieldsFromTemplate } from './utils/templates';
import { getPolicyTemplateById } from '../api';
import { usePolicyTemplatesContext } from './context/PolicyTemplatesContext';

const mapPolicyTemplateDetailsToSearchItems = (templates: PolicyTemplate[]): ListItemProps<string>[] => {
    return templates.map((item) => ({
        title: item.name,
        value: item.id
    }));
};

const getFilteredTemplates = (templates: PolicyTemplate[], searchText: string): PolicyTemplate[] => {
    return templates.filter((template) => template.name.toLowerCase().includes(searchText.toLowerCase()));
};

const SearchTemplates: FC = () => {
    const history = useHistory();
    const match = useRouteMatch();
    const { templates } = usePolicyTemplatesContext();
    const { navigateToCreatePolicyFromTemplate } = useHandleStartPolicyFromTemplate();
    const [searchText, setSearchText] = useState('');

    const searchResults = useMemo(() => getFilteredTemplates(templates, searchText), [templates, searchText]);
    const selectListItems = useMemo(() => mapPolicyTemplateDetailsToSearchItems(searchResults), [searchResults]);

    const handleSearchTextChange = (val: string) => {
        setSearchText(val);
    };

    const handleSelectNonCustomTemplate = (templateId: PolicyTemplate['id']) => {
        history.push(`${match.url}/Create/${templateId}`);
    };

    const handleSelectCustomTemplate = async (templateId: PolicyTemplate['id']) => {
        const template = await getPolicyTemplateById(templateId);

        navigateToCreatePolicyFromTemplate(template.id, getPolicyFieldsFromTemplate(template));
    };

    const handleSelect = async (value?: string | string[]) => {
        // value will never be an array as we are using single select
        if (value && typeof value === 'string') {
            setSearchText('');

            const chosenResult = searchResults.find((template) => template.id === value);
            if (chosenResult) {
                if (chosenResult.typeId === PolicyTemplateStaticCategory.CUSTOM) {
                    await handleSelectCustomTemplate(value);
                } else {
                    handleSelectNonCustomTemplate(value);
                }
            }
        }
    };

    return (
        <div className="mr-2 p-2 flex-1">
            <SelectList
                fullMenuWidth
                items={selectListItems}
                onChange={handleSelect}
                triggerProps={{ className: 'h-full w-full', text: searchText, onChange: handleSearchTextChange }}
                TriggerComponent={Search}
                menuProps={{ disableAutoFocus: true }}
            />
        </div>
    );
};

export default SearchTemplates;
